import React, { useState } from 'react';
import type { ProjectListItem } from '../../services/adminApi';
import { createProject, deleteProject } from '../../services/adminApi';
import styles from './AdminCrud.module.css';

interface ProjectListProps {
  projects: ProjectListItem[];
  isLoading: boolean;
  error: string | null;
  onOpenProject: (id: string) => void;
  onRefresh: () => void;
}

/** Projects table plus the "new project" form (project + client account) */
const ProjectList: React.FC<ProjectListProps> = ({
  projects,
  isLoading,
  error,
  onOpenProject,
  onRefresh,
}) => {
  const [companyName, setCompanyName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handle_create = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    if (!companyName.trim() || !username.trim() || !password) {
      setFormError('Company name, username and password are required');
      return;
    }

    setIsCreating(true);
    try {
      await createProject({
        company_name: companyName.trim(),
        username: username.trim(),
        password,
      });
      setCompanyName('');
      setUsername('');
      setPassword('');
      onRefresh();
    } catch (err) {
      setFormError((err as Error).message || 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handle_delete = async (project: ProjectListItem) => {
    const confirmed = window.confirm(
      `Delete project "${project.company_name}" with all its sections, options and the client account? This cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    setDeletingId(project.id);
    try {
      await deleteProject(project.id);
      onRefresh();
    } catch (err) {
      setFormError((err as Error).message || 'Failed to delete project');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className={styles.card}>
        <h2 className={styles.card_title}>New project</h2>
        <form onSubmit={handle_create}>
          <div className={styles.form_grid}>
            <div className={styles.field}>
              <span className={styles.field_label}>Company name</span>
              <input
                className={styles.input}
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.field_label}>Client username</span>
              <input
                className={styles.input}
                value={username}
                autoComplete="off"
                onChange={(event) => setUsername(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.field_label}>Client password</span>
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
          </div>

          {formError && <p className={styles.error_text}>{formError}</p>}

          <div className={styles.actions_row}>
            <button type="submit" className={styles.button_primary} disabled={isCreating}>
              {isCreating ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>

      <div className={styles.card}>
        <h2 className={styles.card_title}>Projects</h2>

        {isLoading && <p className={styles.muted}>Loading projects…</p>}
        {error && <p className={styles.error_text}>{error}</p>}
        {!isLoading && !error && projects.length === 0 && (
          <p className={styles.muted}>No projects yet — create the first one above.</p>
        )}

        {projects.length > 0 && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Company</th>
                <th>Client username</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr
                  key={project.id}
                  className={styles.row_clickable}
                  onClick={() => onOpenProject(project.id)}
                >
                  <td>{project.company_name}</td>
                  <td>{project.client_username ?? '—'}</td>
                  <td>{project.created_at}</td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.button_danger} ${styles.button_small}`}
                      disabled={deletingId === project.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        handle_delete(project);
                      }}
                    >
                      {deletingId === project.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default ProjectList;
